import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function IslingtonCouncilPage() {
  const faqItems = [
    {
      question: 'Islington spends six times its DfT capital allocation — can I still claim?',
      answer: 'Yes. The DfT Spend scorecard is GREEN, but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Islington is AMBER overall because road condition remains a concern: 57% of B and C roads are in RED or amber condition, the council admits its RED repairs are matched by amber deterioration elsewhere, and 68% of maintenance spend in 2024/25 was reactive.',
    },
    {
      question: 'Only 2.53% of U-roads are RED — does that weaken my claim?',
      answer: 'It means you cannot rely on a high network-wide RED percentage alone — but claims do not turn on borough averages. U-roads make up 216km (82% of Islington\'s 262km road network) and are surveyed only every two years. The transparency report publishes RED share only for U-roads, not amber or green. Your claim still lives on whether the specific pothole was identified, inspected and repaired within reasonable time — especially where prior reports or photos show the defect predated the council\'s response.',
    },
    {
      question: 'What does the biennial U-road survey gap mean for Section 58?',
      answer: 'Islington\'s own report states boroughwide surveys of unclassified roads "are planned to be repeated every two years" — separate from annual Metis data covering only 46.1km of A, B and C roads. A council invoking Section 58 must show a reasonable system for knowing road condition. For incidents between U-road surveys, the council cannot point to contemporaneous network-level condition data for the road class that makes up four-fifths of the borough.',
    },
    {
      question: 'Does the council\'s "repair treadmill" admission help my claim?',
      answer: 'Yes. Islington states that "the length of road in \'red\' condition we have been able to repair is matched by a similar length of \'amber\' condition road that has deteriorated." That is a formal admission the network is not improving — deterioration keeps pace with repair. Combined with 68% reactive spend in 2024/25 and 10,063 potholes filled in five years, it supports the argument that defects routinely form between inspections.',
    },
    {
      question: 'No condition data exists for 2020–2022 — does that matter?',
      answer: 'It can. Metis condition data for A, B and C roads begins in 2023, and the boroughwide survey including U-roads was commissioned in 2023/24. For incidents in 2020, 2021 or 2022, there is no published network condition benchmark in Islington\'s transparency report. That does not automatically win a claim, but it limits what the council can cite from its own published records about network condition at the time of your incident.',
    },
    {
      question: 'Does daily cyclic inspection mean Islington always knows about potholes?',
      answer: 'Not necessarily. The council\'s in-house Highways Operations Team repairs defects following "daily cyclic inspections" — but 1,896 potholes were still filled in 2024/25 alone, and the council acknowledges poorly maintained highways "can pose risks for users and have costly impacts regarding insurance claims." Section 58 asks whether the specific defect was reasonably dealt with, not whether inspections happen in principle. Prior reports and dated photos remain decisive.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Islington Pothole Claims | Repair Treadmill Admission | Fixtyer"
        description="Islington Council: AMBER DfT rating despite GREEN spend. The council admits every RED road repaired is matched by amber deterioration, 57% of B/C roads need maintenance, and 82% of the network is U-roads surveyed every two years. See what this means for your claim."
        canonical="/councils/islington"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Islington', url: '/councils/islington' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Islington Council"
        councilSlug="islington"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/islington"
        headline="Islington Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Islington</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Islington: For Every Red Road Fixed, Another Slips to Amber
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Islington earns a GREEN spend scorecard — projecting £2.225m capital spend against 
            £368,000 from the DfT in 2025/26. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> because the council admits repairs 
            merely keep pace with deterioration, <strong className="text-rose-400">57% of B and C 
            roads need maintenance now or soon</strong>, and on a dense 262km inner-London network 
            where <strong className="text-amber-400">82% of roads are U-roads surveyed only every 
            two years</strong>, 68% of 2024/25 spend was still reactive.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">57%</div>
            <div className="text-white font-medium mt-2">B and C roads in RED or amber (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              10% RED plus 47% amber across 23km of classified local roads — while the council 
              reports the RED share "has been consistent" because amber roads keep deteriorating 
              as fast as repairs land.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Metis survey data from 2023 onwards for A, B and C roads — plus a boroughwide survey 
            commissioned in 2023/24 for unclassified roads
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">262km of roads in a dense urban borough</h3>
            <div className="grid sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">23km</div>
                <div className="text-sm text-surface-600">A roads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">23km</div>
                <div className="text-sm text-surface-600">B and C roads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">216km</div>
                <div className="text-sm text-surface-600">U roads (82%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">472.5km</div>
                <div className="text-sm text-surface-600">Footways</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Condition data from Metis covers only 46.1km of classified roads annually since 2023. 
              The boroughwide survey including 216km of U-roads is planned every two years.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (23km): RED share flat at 9%</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020–2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">38%</td>
                    <td className="px-4 py-2 text-center">53%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">39%</td>
                    <td className="px-4 py-2 text-center font-bold">52%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED condition has not budged — but amber roads rose from 38% to 39%, meaning nearly 
              half of A-road kilometres already need maintenance soon.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (23km): 57% in RED or amber, unchanged</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020–2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">47%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">47%</td>
                    <td className="px-4 py-2 text-center font-bold">43%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Identical figures two years running — not improvement, but stasis. The council's own 
              explanation: repairs to RED roads are offset by amber roads deteriorating at the same rate.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <div className="p-6">
              <h3 className="font-semibold text-brand-900 mb-4">U-roads (216km): RED only, surveyed biennially</h3>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020–2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">2.53%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">2.53%</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-surface-600 text-sm">
                The transparency report publishes RED percentage only for U-roads — not amber or green. 
                At 2.53%, that is roughly 5.5km of residential streets in poor condition at the last 
                two surveys, on a network measured only every other year.
              </p>
            </div>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, Reactive Reality</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£368k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.225m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">68%</div>
                <div className="text-brand-300 text-sm">Reactive maintenance share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Islington invests well beyond its DfT allocation — yet in 2024/25,{' '}
              <span className="text-rose-400">68% of maintenance spend was reactive</span>, and the 
              council admits RED repairs are merely keeping pace with amber deterioration. The 
              problem is not the chequebook alone. It is whether the specific defect on your road 
              was caught in time.
            </p>
          </div>
        </div>
      </section>

      {/* The 216km U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 216km Survey Gap
          </h2>
          <p className="text-surface-600 mb-8">
            82% of Islington's road network is unclassified — on a different survey schedule to classified roads
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Two Survey Systems</h3>
              <p className="text-surface-600 text-sm mb-3">
                Metis provides annual condition data for all 46.1km of A, B and C roads since 2023. 
                Separately, Islington commissioned a boroughwide survey in 2023/24 covering 235km of 
                unclassified roads and nearly 500km of pavements.
              </p>
              <p className="text-surface-600 text-sm">
                U-road surveys "are planned to be repeated every two years" — meaning no 
                network-level condition benchmark for residential streets in the off years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Daily Inspections vs Network Surveys</h3>
              <p className="text-amber-800 text-sm mb-3">
                The in-house Highways Operations Team carries out repairs following "daily cyclic 
                inspections" — but cyclic inspection is not the same as a condition survey. Defects 
                can form, worsen and cause damage between inspection cycles.
              </p>
              <p className="text-amber-800 text-sm">
                For 2025/26 the council plans just 1.46km of carriageway resurfacing and 0.4km of 
                pavement improvements across 262km of roads and 472.5km of pavements — reactive 
                patching carries most of the load.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Islington must maintain its roads. Under 
              Section 58, it can defend a claim by showing it took reasonable care. For most 
              Islington drivers, that turns on U-roads. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it a survey year or a gap year?</li>
              <li>• If only 2.53% of U-roads were RED at the last survey, what was done about defects on the other 97.47%?</li>
              <li>• Does a daily cyclic inspection record show your specific pothole was identified before your incident?</li>
              <li>• Were there prior reports (FixMyStreet, council fault reports) that put the council on notice?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge from a survey it runs every two years 
              — then point to aggregate spend figures as proof the specific defect was managed.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            10,063 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled and revenue spend — from the council's own transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend on potholes</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% of revenue spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,989</td>
                  <td className="px-6 py-4 text-center">£119,340</td>
                  <td className="px-6 py-4 text-center">4.60%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,821</td>
                  <td className="px-6 py-4 text-center">£109,260</td>
                  <td className="px-6 py-4 text-center">4.60%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,838</td>
                  <td className="px-6 py-4 text-center">£110,280</td>
                  <td className="px-6 py-4 text-center">4.10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">1,519</td>
                  <td className="px-6 py-4 text-center">£91,140</td>
                  <td className="px-6 py-4 text-center">3.30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,896</td>
                  <td className="px-6 py-4 text-center">£125,136</td>
                  <td className="px-6 py-4 text-center">4.40%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,063</td>
                  <td className="px-6 py-4 text-center font-bold">£555,156</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~5.5 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Islington fills around 5.5 potholes daily on a 262km 
                network. The 2024/25 rebound to 1,896 — up 25% from 2023/24's low of 1,519 — 
                came despite 32% preventative spend that year. Defects are still forming between repairs.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Insurance Admission</h3>
              <p className="text-amber-800 text-sm">
                Islington acknowledges that "a poorly maintained public highway can pose risks for 
                users and have costly impacts regarding insurance claims." That is the council's 
                own framing of why maintenance matters — and why your claim is not frivolous when 
                a defect was left unrepaired.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Repair Treadmill Admission
          </h2>
          <p className="text-surface-600 mb-8">
            What Islington says about its own road condition — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In recent years, the percentage of principal and non-principal roads in 'red' condition has been consistent, which means that on average the length of road in 'red' condition we have been able to repair is matched by a similar length of 'amber' condition road that has deteriorated."
              source="Islington Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Without pavement and road renewals, conditions can deteriorate to a point where it becomes even more costly to repair pavement and road surfaces."
              source="Islington Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The data from the surveys that Islington commissioned independently provided a better and more accurate understanding of our highway conditions, particularly for the unclassified roads not covered in the Metis survey."
              source="Islington Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Islington is not claiming its roads are getting better. It states explicitly that 
                repair output matches deterioration input — a treadmill, not recovery. That undercuts 
                any argument that aggregate maintenance spend has resolved network condition.
              </p>
              <p className="text-surface-600 text-sm">
                For your claim, it reframes the question: not "are the roads maintained in general?" 
                but "was this specific defect identified and dealt with before it damaged your vehicle?"
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forward Plans</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• 1.46km of carriageway resurfacing across nine named roads</li>
                <li>• 0.4km of pavement improvements at eight locations</li>
                <li>• Thermal Road Repairs and JCB Pothole Pro-Plus trials</li>
                <li>• Structural works on Mountview Road Bridge</li>
              </ul>
              <p className="text-amber-800 text-sm mt-3">
                On a 262km road network, planned resurfacing covers a fraction of one percent — 
                most defects will still be handled reactively.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Maintenance Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue spend with preventative vs reactive split — from Table 3 of the transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£368k</td>
                  <td className="px-4 py-3 text-center">£2,225k</td>
                  <td className="px-4 py-3 text-center">£2,835k</td>
                  <td className="px-4 py-3 text-center">44%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">56%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£113k</td>
                  <td className="px-4 py-3 text-center">£1,502k</td>
                  <td className="px-4 py-3 text-center">£2,817k</td>
                  <td className="px-4 py-3 text-center">32%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">68%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£105k</td>
                  <td className="px-4 py-3 text-center">£1,635k</td>
                  <td className="px-4 py-3 text-center">£2,775k</td>
                  <td className="px-4 py-3 text-center">32%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">68%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£1,603k</td>
                  <td className="px-4 py-3 text-center">£2,713k</td>
                  <td className="px-4 py-3 text-center">34%</td>
                  <td className="px-4 py-3 text-center">66%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£1,799k</td>
                  <td className="px-4 py-3 text-center">£2,377k</td>
                  <td className="px-4 py-3 text-center">41%</td>
                  <td className="px-4 py-3 text-center">59%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£1,916k</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">2,6614*</td>
                  <td className="px-4 py-3 text-center">35%</td>
                  <td className="px-4 py-3 text-center">65%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-sm italic">
            *2020/21 revenue spend is published in the report as "2,6614" (£,000s) — the figure appears 
            to contain a typographical error and is reproduced here verbatim.
          </p>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Islington is not a failing authority — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend multiples above DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Daily cyclic inspections by an in-house Highways Operations Team
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Low U-road RED share (2.53%) at last boroughwide survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management via Sustaining our Streets policy and Bridgestation for structures
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Only 9% of A-roads in RED — stable, not worsening
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a organised Section 58 defence. Broad "the roads are terrible" arguments will not suffice.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 57% of B/C roads in RED or amber, unchanged since 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits repair treadmill — RED fixes matched by amber deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 68% reactive spend in 2024/25 — patching, not preventing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 216km of U-roads surveyed only every two years — no data for gap years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10,063 potholes filled in five years — defects still forming between repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No condition data at all for 2020–2022 in the published report
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented inspection regimes, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Islington fault reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the biennial survey gap is your strongest structural argument</li>
              <li>• Timing relative to the 2023/24 boroughwide survey — was your road even measured before your incident?</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Islington's own transparency data — including the repair treadmill admission — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Islington?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No biennial survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No repair-treadmill citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road condition stasis documented</li>
                <li>✅ Biennial U-road survey gap argued</li>
                <li>✅ 10,063 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Islington</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=islington"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Islington Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.islington.gov.uk/roads/highways-capital-programme"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Islington Council Local Highways Maintenance Transparency Report (30 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
